import { Injectable } from '@angular/core';
import { BaseFormService } from 'ag-common-svc/lib/utils/base-form-service';
import { Attendee } from 'ag-common-svc/lib/utils/attendees';
import {
  AttendeeKeys,
  AttendeeTypeMap,
  ChangeSourceType,
  ConferenceLogBulkInviteeOutcomeStatus,
  InviteeStatus,
  InviteeStatusMap,
  QualifiedAsMap,
  RegistrantKeys,
  RegistrantModelKeys,
  RegistrationTypeMap,
  UploadFilePayload,
  UploadMediaPayloadKeys,
} from 'ag-common-lib/public-api';
import {
  buildFileName,
  ConferenceBulkInviteeOutcomeStatusService,
  ConferenceRegistrantsService,
  ConferenceService,
} from 'ag-common-svc/public-api';
import { ToastrService } from 'ngx-toastr';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { Workbook } from 'exceljs';
import { adjustColumnWidth } from 'ag-common-svc/lib/utils/exceljs.util';
import { fileToUIBase64 } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.utils';
import { CloudFunctionsService } from 'ag-common-svc/lib/services/cloud-functions.service';
import { AgencyService } from 'ag-common-svc/lib/services/agency.service';
import { firstValueFrom, take } from 'rxjs';

@Injectable()
export class UpdateInviteeOutcomeStatusModalService extends BaseFormService<Partial<Attendee>> {
  private mgaNamesByIdCache: Map<string, string> | null = null;

  constructor(
    private readonly conferenceRegistrantsService: ConferenceRegistrantsService,
    private readonly toastrService: ToastrService,
    private readonly cloudFunctionsService: CloudFunctionsService,
    private readonly conferenceBulkInviteeOutcomeStatusService: ConferenceBulkInviteeOutcomeStatusService,
    private readonly agencyService: AgencyService,
    private readonly conferenceService: ConferenceService,
  ) {
    super();
  }

  async updateInviteeOutcomeStatus(
    conferenceDbId: string,
    attendees: Attendee[],
    outcomeStatus: InviteeStatus,
  ): Promise<void> {
    if (!conferenceDbId || !attendees?.length || !outcomeStatus) {
      return;
    }

    this.startProgress();

    try {
      const registrantsToUpdate = this.extractRegistrantsData(attendees);

      if (!registrantsToUpdate?.length) {
        return;
      }

      await this.updateRegistrants(conferenceDbId, registrantsToUpdate, outcomeStatus);

      await this.logBulkUpdate(conferenceDbId, attendees, registrantsToUpdate, outcomeStatus);

      this.toastrService.success(
        `Invitee Outcome updated to ${InviteeStatusMap.get(outcomeStatus)} for ${attendees.length} invitees.`,
      );
    } catch (error) {
      this.toastrService.error('Failed to update Invitee Outcome for the selected invitees.');
      throw error;
    } finally {
      this.stopProgress();
    }
  }

  private extractRegistrantsData(attendees: Attendee[]): Array<{
    registrantDbId: string;
    previousOutcomeStatus: InviteeStatus | null;
  }> {
    return attendees
      .map(attendee => attendee?.registrant)
      .filter(Boolean)
      .map(registrant => {
        const registrantDbId = registrant?.[BaseModelKeys.dbId];
        const previousOutcomeStatus =
          registrant?.[RegistrantModelKeys.data]?.[RegistrantKeys.inviteeOutcomeStatus] ?? null;

        return {
          registrantDbId,
          previousOutcomeStatus,
        };
      })
      .filter(item => item.registrantDbId);
  }

  private async updateRegistrants(
    conferenceDbId: string,
    registrantsToUpdate: Array<{ registrantDbId: string }>,
    outcomeStatus: InviteeStatus,
  ): Promise<void> {
    await Promise.all(
      registrantsToUpdate.map(({ registrantDbId }) =>
        this.conferenceRegistrantsService.update(
          conferenceDbId,
          registrantDbId,
          {
            [RegistrantModelKeys.data]: {
              [RegistrantKeys.inviteeOutcomeStatus]: outcomeStatus,
            },
          },
          ChangeSourceType.regularArm,
          true,
        ),
      ),
    );
  }

  private getBulkUpdatedChangesBlob = async (
    conferenceDbId: string,
    attendees: Attendee[],
    registrantsToUpdate: Array<{
      registrantDbId: string;
      previousOutcomeStatus: InviteeStatus | null;
    }>,
    newOutcomeStatus: InviteeStatus,
  ) => {
    const previousOutcomeStatusByRegistrantId = new Map(
      registrantsToUpdate.map(item => [item.registrantDbId, item.previousOutcomeStatus]),
    );
    const newOutcomeStatusLabel = InviteeStatusMap.get(newOutcomeStatus) ?? newOutcomeStatus;
    const mgaNamesById = await this.loadMgaNamesById();
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Outcome Status Bulk Updates');
    worksheet.columns = [
      { header: 'First Name', key: 'firstName' },
      { header: 'Last Name', key: 'lastName' },
      { header: 'Attendee Type', key: 'attendeeType' },
      { header: 'Invitee Email', key: 'inviteeEmail' },
      { header: 'Registration Type', key: 'registrationType' },
      { header: 'Qualified As', key: 'qualifiedAs' },
      { header: 'MGA', key: 'mga' },
      { header: 'Agent ID', key: 'agentId' },
      { header: 'Initial Outcome Status', key: 'previousOutcomeStatus' },
      { header: 'Resulted Outcome Status', key: 'newOutcomeStatus' },
      { header: 'Conference DbId', key: 'conferenceDbId' },
      { header: 'Registrant DbId', key: 'registrantDbId' },
    ];

    const header = worksheet.getRow(1);
    header.font = { bold: true };

    attendees?.forEach(attendee => {
      const registrant = attendee?.registrant;
      const registrantDbId = registrant?.[BaseModelKeys.dbId];
      const registrantData = registrant?.[RegistrantModelKeys.data] ?? {};

      if (!registrantDbId) {
        return;
      }

      const previousOutcomeStatus = previousOutcomeStatusByRegistrantId.get(registrantDbId);
      const previousOutcomeStatusLabel =
        previousOutcomeStatus != null ? (InviteeStatusMap.get(previousOutcomeStatus) ?? previousOutcomeStatus) : '';

      worksheet.addRow({
        firstName: registrantData?.[RegistrantKeys.firstName] ?? '',
        lastName: registrantData?.[RegistrantKeys.lastName] ?? '',
        attendeeType: AttendeeTypeMap.get(attendee?.[AttendeeKeys.attendeeType]) ?? '',
        inviteeEmail: registrantData?.[RegistrantKeys.inviteeEmail] ?? '',
        registrationType: RegistrationTypeMap.get(registrantData?.[RegistrantKeys.registrationType]) ?? '',
        qualifiedAs: QualifiedAsMap.get(registrantData?.[RegistrantKeys.qualifiedAs]) ?? '',
        mga: this.resolveMgaName(registrantData?.[RegistrantKeys.mgaId], mgaNamesById),
        agentId: registrantData?.[RegistrantKeys.agentId] ?? '',
        previousOutcomeStatus: previousOutcomeStatusLabel,
        newOutcomeStatus: newOutcomeStatusLabel,
        conferenceDbId,
        registrantDbId,
      });
    });

    adjustColumnWidth(worksheet);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/octet-stream',
    });

    return blob;
  };

  private resolveMgaName(mgaId: string | undefined | null, mgaNamesById: Map<string, string> | null): string {
    if (!mgaId) {
      return '';
    }

    return mgaNamesById?.get(mgaId) ?? mgaId;
  }

  private async loadMgaNamesById(): Promise<Map<string, string> | null> {
    if (this.mgaNamesByIdCache) {
      return this.mgaNamesByIdCache;
    }

    const namesById = await firstValueFrom(this.agencyService.agenciesNamesByAgencyId$.pipe(take(1))).catch(() => null);
    if (!namesById) {
      return null;
    }

    this.mgaNamesByIdCache = new Map<string, string>(Object.entries(namesById));
    return this.mgaNamesByIdCache;
  }

  private async logBulkUpdate(
    conferenceDbId: string,
    attendees: Attendee[],
    registrantsToUpdate: Array<{
      registrantDbId: string;
      previousOutcomeStatus: InviteeStatus | null;
    }>,
    newOutcomeStatus: InviteeStatus,
  ): Promise<void> {
    try {
      const previousOutcomeStatus = null; // in issue change only null outcome status
      const registrantDbIds = registrantsToUpdate.map(r => r.registrantDbId);
      if (!conferenceDbId || !registrantDbIds?.length || !newOutcomeStatus) {
        throw new Error('Missing required fields');
      }
      const bulkUpdatedChangesBlob = await this.getBulkUpdatedChangesBlob(
        conferenceDbId,
        attendees,
        registrantsToUpdate,
        newOutcomeStatus,
      );
      const wasabiPath = await this.uploadSourceFile(conferenceDbId, bulkUpdatedChangesBlob);

      const logData: ConferenceLogBulkInviteeOutcomeStatus = {
        conferenceDbId,
        previousOutcomeStatus,
        newOutcomeStatus,
        registrantDbIds,
        wasabiPath,
      };
      await this.conferenceBulkInviteeOutcomeStatusService.create(logData);
    } catch (error) {
      console.error('Failed to log bulk invitee outcome status update:', error);
    }
  }

  private async uploadSourceFile(conferenceDbId: string, sourceFile: Blob): Promise<string> {
    const now = new Date();
    const filePath = ['conferences', conferenceDbId, 'invitee-outcome-status-bulk-updates'].filter(Boolean).join('/');
    const eventName = await this.conferenceService.getConferenceEventName(conferenceDbId);
    const safeEventName = (eventName ?? '')
      .replace(/\s+/g, '_')
      .replace(/[^\w_]/g, '')
      .trim();
    const fileLabel = `Outcome_Status_Bulk_Updated_Changes_${safeEventName}`;
    const dateLabel = now.toLocaleDateString('en');
    const fileName = buildFileName(fileLabel, dateLabel);
    const contentType = sourceFile.type;
    const base64 = await fileToUIBase64(sourceFile)
      .then(fileBase64 => {
        return fileBase64.split(',')[1];
      })
      .catch(() => {
        return null;
      });

    if (!base64) {
      return null;
    }
    const payload: UploadFilePayload = {
      [UploadMediaPayloadKeys.filePath]: filePath,
      [UploadMediaPayloadKeys.mediaData]: base64,
      [UploadMediaPayloadKeys.contentType]: contentType,
      [UploadMediaPayloadKeys.metadata]: {
        fileName,
      },
    };

    return this.cloudFunctionsService
      .uploadFile(payload)
      .then(sourceFileKey => {
        this.toastrService.success('Excel file created and uploaded successfully');
        return sourceFileKey;
      })
      .catch(() => {
        this.toastrService.error('Excel file upload failed: ');
        return null;
      });
  }
}
